/**
 * IndexedDB Manager for Dynamic QR Code System
 * Handles secure storage of QR code hashes and validation data
 */

class IndexedDBManager {
    constructor() {
        this.dbName = 'SunquickQRDB';
        this.dbVersion = 1;
        this.storeName = 'qr_codes';
        this.db = null;
    }

    /**
     * Initialize IndexedDB with automatic corruption recovery
     */
    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => {
                const errorMessage = request.error?.message || '';
                console.error('IndexedDB failed to open:', errorMessage);

                // Check if it's a corruption error (backing store, internal error, corrupt)
                if (errorMessage.includes('backing store') ||
                    errorMessage.includes('Internal error') ||
                    errorMessage.includes('corrupt')) {

                    console.warn('⚠️ Database appears corrupted. Attempting to delete and recreate...');

                    // Delete the corrupted database
                    const deleteRequest = indexedDB.deleteDatabase(this.dbName);

                    deleteRequest.onsuccess = () => {
                        console.log('✅ Corrupted database deleted. Retrying initialization...');

                        // Retry opening the database
                        const retryRequest = indexedDB.open(this.dbName, this.dbVersion);

                        retryRequest.onsuccess = () => {
                            this.db = retryRequest.result;
                            console.log('✅ IndexedDB reopened successfully after cleanup');
                            resolve(this.db);
                        };

                        retryRequest.onerror = () => {
                            console.error('❌ Failed to reopen database after cleanup');
                            reject(retryRequest.error);
                        };

                        retryRequest.onupgradeneeded = (event) => {
                            const db = event.target.result;

                            if (!db.objectStoreNames.contains(this.storeName)) {
                                const store = db.createObjectStore(this.storeName, {
                                    keyPath: 'qr_hash'
                                });

                                store.createIndex('fingerprint', 'fingerprint', { unique: false });
                                store.createIndex('timestamp', 'timestamp', { unique: false });
                                store.createIndex('status', 'status', { unique: false });

                                console.log('QR codes object store recreated after corruption');
                            }
                        };
                    };

                    deleteRequest.onerror = () => {
                        console.error('❌ Failed to delete corrupted database');
                        reject(request.error);
                    };

                } else {
                    // Not a corruption error, reject normally
                    reject(request.error);
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('✅ IndexedDB opened successfully');
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create object store for QR codes
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, {
                        keyPath: 'qr_hash'
                    });

                    // Create indexes
                    store.createIndex('fingerprint', 'fingerprint', { unique: false });
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                    store.createIndex('status', 'status', { unique: false });

                    console.log('QR codes object store created');
                }
            };
        });
    }

    /**
     * Store QR code data in IndexedDB
     */
    async storeQRData(qrHash, fingerprint, additionalData = {}) {
        if (!this.db) {
            await this.init();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);

            const qrData = {
                qr_hash: qrHash,
                fingerprint: fingerprint,
                timestamp: Date.now(),
                status: 'active',
                created_at: new Date().toISOString(),
                ...additionalData
            };

            const request = store.put(qrData);

            request.onsuccess = () => {
                console.log('QR data stored successfully:', qrHash);
                resolve(qrData);
            };

            request.onerror = () => {
                console.error('Failed to store QR data:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Retrieve QR code data from IndexedDB
     */
    async getQRData(qrHash) {
        if (!this.db) {
            await this.init();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.get(qrHash);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                console.error('Failed to retrieve QR data:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Check if QR code exists in IndexedDB
     */
    async hasQRCode(qrHash) {
        try {
            const data = await this.getQRData(qrHash);
            return !!data;
        } catch (error) {
            console.error('Error checking QR code existence:', error);
            return false;
        }
    }

    /**
     * Update QR code status
     */
    async updateQRStatus(qrHash, status, additionalData = {}) {
        if (!this.db) {
            await this.init();
        }

        return new Promise(async (resolve, reject) => {
            try {
                const existingData = await this.getQRData(qrHash);
                
                if (!existingData) {
                    reject(new Error('QR code not found'));
                    return;
                }

                const transaction = this.db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);

                const updatedData = {
                    ...existingData,
                    status: status,
                    updated_at: new Date().toISOString(),
                    ...additionalData
                };

                const request = store.put(updatedData);

                request.onsuccess = () => {
                    console.log('QR status updated:', qrHash, status);
                    resolve(updatedData);
                };

                request.onerror = () => {
                    console.error('Failed to update QR status:', request.error);
                    reject(request.error);
                };
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Remove QR code from IndexedDB
     */
    async removeQRCode(qrHash) {
        if (!this.db) {
            await this.init();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.delete(qrHash);

            request.onsuccess = () => {
                console.log('QR code removed:', qrHash);
                resolve(true);
            };

            request.onerror = () => {
                console.error('Failed to remove QR code:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get all stored QR codes
     */
    async getAllQRCodes() {
        if (!this.db) {
            await this.init();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                console.error('Failed to get all QR codes:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Clean up old QR codes (older than specified days)
     */
    async cleanupOldQRCodes(daysOld = 7) {
        if (!this.db) {
            await this.init();
        }

        const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const index = store.index('timestamp');
            const range = IDBKeyRange.upperBound(cutoffTime);
            const request = index.openCursor(range);

            let deletedCount = 0;

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    cursor.delete();
                    deletedCount++;
                    cursor.continue();
                } else {
                    console.log(`Cleaned up ${deletedCount} old QR codes`);
                    resolve(deletedCount);
                }
            };

            request.onerror = () => {
                console.error('Failed to cleanup old QR codes:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Generate browser fingerprint
     * This fingerprint is sent to the backend and used consistently across all operations
     */
    generateBrowserFingerprint() {
        const components = [
            navigator.userAgent,
            navigator.language,
            screen.width + 'x' + screen.height,
            screen.colorDepth,
            new Date().getTimezoneOffset(),
            navigator.platform,
            navigator.cookieEnabled
        ];

        // Create a simple hash
        let hash = 0;
        const str = components.join('|');
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }

        return Math.abs(hash).toString(36);
    }

    /**
     * Validate stored QR data against server
     */
    async validateWithServer(qrHash) {
        try {
            const storedData = await this.getQRData(qrHash);
            
            if (!storedData) {
                return {
                    valid: false,
                    reason: 'QR code not found in local storage'
                };
            }

            // For rescan QRs, add a small delay to ensure server DB is ready
            if (storedData.additional_data && storedData.additional_data.is_rescan) {
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            // Call server validation endpoint
            const response = await fetch('/api/qr/verify-indexeddb', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                body: JSON.stringify({
                    qr_hash: qrHash,
                    fingerprint: storedData.fingerprint,
                    timestamp: storedData.timestamp
                })
            });

            const result = await response.json();
            
            console.log('Server validation result for', qrHash, ':', result);

            if (!result.valid) {
                // Update local status if invalid
                await this.updateQRStatus(qrHash, 'invalid', {
                    invalidation_reason: result.reason
                });
            }

            return result;

        } catch (error) {
            console.error('Server validation failed:', error);
            return {
                valid: false,
                reason: 'Server validation failed'
            };
        }
    }
}

// Export singleton instance
export default new IndexedDBManager();
